<template>
    <section id="projects" class="projects-section">
        <div class="container">
            <div class="section-identifier">
                {{ t.projects.section }}
            </div>

            <div class="projects-heading">
                <div>
                    <p class="eyebrow">
                        <span></span>
                        {{ t.projects.eyebrow }}
                    </p>

                    <h2>
                        {{ t.projects.titleFirst }}
                        <em>{{ t.projects.titleSecond }}</em>
                    </h2>
                </div>

                <p class="projects-description">
                    {{ t.projects.description }}
                </p>
            </div>

            <!-- One cover image per category -->
            <div class="category-grid">
                <button v-for="category in categoryCards"
                        :key="category.name"
                        type="button"
                        class="category-card"
                        @click="openGallery(category.name)">
                    <img :src="category.cover.image"
                         :alt="t.projects.filters[category.name]"
                         loading="lazy" />

                    <div class="category-overlay"></div>

                    <div class="category-information">
                        <span>
                            {{ String(category.total).padStart(2, '0') }}
                            {{ language === 'es' ? 'fotografías' : 'photos' }}
                        </span>

                        <h3>
                            {{ t.projects.filters[category.name] }}
                        </h3>

                        <p>
                            {{ language === 'es' ? 'Ver galería' : 'View gallery' }}
                            <span aria-hidden="true">↗</span>
                        </p>
                    </div>
                </button>
            </div>
        </div>

        <!-- Category gallery -->
        <div v-if="galleryOpen && activeProject"
             class="gallery-modal"
             role="dialog"
             aria-modal="true"
             :aria-label="t.projects.filters[activeCategory]"
             @click.self="closeGallery">
            <button type="button"
                    class="close-button"
                    :aria-label="t.projects.close"
                    @click="closeGallery">
                <span></span>
                <span></span>
            </button>

            <button v-if="activeGallery.length > 1"
                    type="button"
                    class="navigation-button previous"
                    :aria-label="language === 'es'
          ? 'Fotografía anterior'
          : 'Previous photo'"
                    @click="previousImage">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="m15 18-6-6 6-6"></path>
                </svg>
            </button>

            <div class="gallery-content">
                <img :src="activeProject.image"
                     :alt="t.projects.filters[activeCategory]" />

                <div class="gallery-information">
                    <div>
                        <span>{{ t.projects.filters[activeCategory] }}</span>
                    </div>

                    <strong>
                        {{ activeIndex + 1 }} / {{ activeGallery.length }}
                    </strong>
                </div>
            </div>

            <button v-if="activeGallery.length > 1"
                    type="button"
                    class="navigation-button next"
                    :aria-label="language === 'es'
          ? 'Siguiente fotografía'
          : 'Next photo'"
                    @click="nextImage">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="m9 18 6-6-6-6"></path>
                </svg>
            </button>
        </div>
    </section>
</template>

<script>
    import projects, {
        projectCategories
    } from '@/data/projects'

    export default {
        name: 'ProjectsGallery',

        props: {
            t: {
                type: Object,
                required: true
            },

            language: {
                type: String,
                required: true
            }
        },

        data() {
            return {
                projects,
                projectCategories,
                galleryOpen: false,
                activeCategory: null,
                activeIndex: 0
            }
        },

        computed: {
            categoryCards() {
                return this.projectCategories
                    .filter(category => category !== 'all')
                    .map(category => {
                        const categoryProjects = this.projects.filter(
                            project => project.category === category
                        )

                        return {
                            name: category,
                            cover: categoryProjects[0],
                            total: categoryProjects.length
                        }
                    })
                    .filter(category => category.cover)
            },

            activeGallery() {
                if (!this.activeCategory) {
                    return []
                }

                return this.projects.filter(
                    project => project.category === this.activeCategory
                )
            },

            activeProject() {
                return this.activeGallery[this.activeIndex] || null
            }
        },

        watch: {
            galleryOpen(isOpen) {
                document.body.classList.toggle('no-scroll', isOpen)
            }
        },

        mounted() {
            window.addEventListener('keydown', this.handleKeydown)
        },

        beforeUnmount() {
            window.removeEventListener('keydown', this.handleKeydown)
            document.body.classList.remove('no-scroll')
        },

        methods: {
            openGallery(category) {
                this.activeCategory = category
                this.activeIndex = 0
                this.galleryOpen = true
            },

            closeGallery() {
                this.galleryOpen = false
                this.activeCategory = null
                this.activeIndex = 0
            },

            nextImage() {
                this.activeIndex =
                    (this.activeIndex + 1) % this.activeGallery.length
            },

            previousImage() {
                this.activeIndex =
                    (this.activeIndex - 1 + this.activeGallery.length) %
                    this.activeGallery.length
            },

            handleKeydown(event) {
                if (!this.galleryOpen) {
                    return
                }

                if (event.key === 'Escape') {
                    this.closeGallery()
                }

                if (event.key === 'ArrowRight') {
                    this.nextImage()
                }

                if (event.key === 'ArrowLeft') {
                    this.previousImage()
                }
            }
        }
    }
</script>

<style scoped>
    .projects-section {
        padding-block: var(--section-padding);
        color: var(--color-white);
        background-color: var(--color-charcoal-light);
    }

    .section-identifier {
        margin-bottom: 62px;
        color: #aaa69e;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.2em;
        text-transform: uppercase;
    }

    .projects-heading {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 60px;
        margin-bottom: 55px;
    }

    .eyebrow {
        display: flex;
        align-items: center;
        gap: 13px;
        margin-bottom: 25px;
        color: #efa46d;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.21em;
        text-transform: uppercase;
    }

        .eyebrow span {
            width: 32px;
            height: 2px;
            background-color: currentColor;
        }

    h2 {
        max-width: 850px;
        margin: 0;
        font-family: var(--font-heading);
        font-size: clamp(50px, 5.3vw, 82px);
        font-weight: 400;
        letter-spacing: -0.045em;
        line-height: 0.98;
    }

        h2 em {
            display: block;
            color: var(--color-orange);
            font-weight: 400;
        }

    .projects-description {
        max-width: 390px;
        margin: 0 0 5px;
        color: #aaa69e;
        font-size: 16px;
        line-height: 1.75;
    }

    .category-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 17px;
    }

    .category-card {
        position: relative;
        min-height: 390px;
        padding: 0;
        overflow: hidden;
        color: var(--color-white);
        background-color: var(--color-charcoal);
        border: 0;
        text-align: left;
    }

        .category-card img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.6s ease;
        }

        .category-card:hover img {
            transform: scale(1.045);
        }

    .category-overlay {
        position: absolute;
        inset: 0;
        background: linear-gradient( 0deg, rgba(0, 0, 0, 0.85), transparent 60% );
    }

    .category-information {
        position: absolute;
        right: 27px;
        bottom: 25px;
        left: 27px;
    }

        .category-information > span {
            color: #efa46d;
            font-size: 9px;
            font-weight: 800;
            letter-spacing: 0.16em;
            text-transform: uppercase;
        }

        .category-information h3 {
            margin: 8px 0 0;
            font-family: var(--font-heading);
            font-size: 31px;
            font-weight: 400;
        }

        .category-information p {
            display: flex;
            align-items: center;
            gap: 10px;
            margin: 14px 0 0;
            font-size: 9px;
            font-weight: 800;
            letter-spacing: 0.14em;
            text-transform: uppercase;
        }

    .gallery-modal {
        position: fixed;
        z-index: 2000;
        inset: 0;
        display: grid;
        padding: 70px 90px 35px;
        place-items: center;
        background-color: rgba(8, 8, 7, 0.96);
        backdrop-filter: blur(8px);
    }

    .gallery-content {
        max-width: min(1150px, 83vw);
    }

        .gallery-content > img {
            display: block;
            max-width: 100%;
            max-height: 76vh;
            margin: auto;
            object-fit: contain;
        }

    .gallery-information {
        display: flex;
        padding-top: 17px;
        align-items: center;
        justify-content: space-between;
        gap: 25px;
    }

        .gallery-information span {
            color: #efa46d;
            font-size: 10px;
            font-weight: 800;
            letter-spacing: 0.18em;
            text-transform: uppercase;
        }

        .gallery-information strong {
            color: #aaa69e;
            font-size: 11px;
            letter-spacing: 0.12em;
        }

    .close-button,
    .navigation-button {
        position: absolute;
        z-index: 2;
        color: var(--color-white);
        background: transparent;
        border: 1px solid rgba(255, 255, 255, 0.4);
    }

    .close-button {
        top: 22px;
        right: 28px;
        width: 46px;
        height: 46px;
    }

        .close-button span {
            position: absolute;
            top: 21px;
            left: 11px;
            width: 23px;
            height: 2px;
            background-color: var(--color-white);
        }

            .close-button span:first-child {
                transform: rotate(45deg);
            }

            .close-button span:last-child {
                transform: rotate(-45deg);
            }

    .navigation-button {
        top: 50%;
        display: grid;
        width: 54px;
        height: 54px;
        place-items: center;
        transform: translateY(-50%);
    }

        .navigation-button.previous {
            left: 25px;
        }

        .navigation-button.next {
            right: 25px;
        }

        .navigation-button svg {
            width: 25px;
            fill: none;
            stroke: currentColor;
            stroke-linecap: round;
            stroke-linejoin: round;
            stroke-width: 1.7;
        }

    @media (max-width: 900px) {
        .projects-heading {
            display: block;
        }

        .projects-description {
            margin-top: 30px;
        }

        .category-grid {
            grid-template-columns: repeat(2, 1fr);
        }

        .gallery-modal {
            padding-inline: 70px;
        }
    }

    @media (max-width: 580px) {
        .section-identifier {
            margin-bottom: 40px;
        }

        .category-grid {
            grid-template-columns: 1fr;
        }

        .category-card {
            min-height: 340px;
        }

        .gallery-modal {
            padding: 70px 15px 25px;
        }

        .gallery-content {
            max-width: 100%;
        }

        .navigation-button {
            top: auto;
            bottom: 18px;
        }

            .navigation-button.previous {
                left: 15px;
            }

            .navigation-button.next {
                right: 15px;
            }

        .gallery-information {
            padding-inline: 70px;
        }
    }
</style>